<template>
  <div class="pdf-viewer-app">
    <div class="pdf-toolbar">
      <div class="button-group">
        <button 
          @click="undoLastAction" 
          class="btn-undo" 
          :disabled="historyStack.length === 0"
          :class="{ 'disabled': historyStack.length === 0 }"
        >
          ↩ Undo
        </button>
      </div>

      <div class="divider"></div>

      <div class="button-group">
        <button @click="applyStrikeThrough" class="btn-strike">S Strike</button>
        <button @click="applyComment" class="btn-comment">💬 Comment</button>
        <button @click="applyStrikeWithComment" class="btn-strike-comment">❌ Strike & Comment</button>
      </div>
    </div>

    <div class="pdf-canvas">
      <div ref="editorRef" class="pdf-page">
        <h2 style="text-align: center; margin-bottom: 30px;">INDEPENDENT CONTRACTOR AGREEMENT</h2>
        <p>This Agreement is entered into as of <strong>March 5, 2026</strong>, by and between the Client and the Contractor.</p>
        <br>
        <p><strong>1. Services:</strong> The Contractor agrees to perform the following services: Web development and design for the new application. The project will take exactly 3 weeks to complete.</p>
        <p><strong>2. Payment:</strong> The Client agrees to pay the Contractor the sum of $5,000 for the services rendered as outlined in Exhibit A. Payment will be made within 30 days of invoice receipt.</p>
        
        <h3 style="margin-top: 40px; color: #2c3e50;">Exhibit A: Pricing Details</h3>
        <table class="pricing-table">
          <thead>
            <tr>
              <th>Item Description</th>
              <th>Hours / Qty</th>
              <th>Rate</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>UI/UX Wireframing & Design</td>
              <td>40</td>
              <td>$50/hr</td>
              <td>$2,000</td>
            </tr>
            <tr>
              <td>Frontend Vue.js Development</td>
              <td>60</td>
              <td>$50/hr</td>
              <td>$3,000</td>
            </tr>
            <tr style="font-weight: bold; background-color: #f8f9fa;">
              <td colspan="3" style="text-align: right;">Total Contract Value:</td>
              <td>$5,000</td>
            </tr>
          </tbody>
        </table>

        <br><br>
        <div style="display: flex; justify-content: space-between; margin-top: 50px;">
          <div>
            <p>_______________________</p>
            <p><strong>Client Signature</strong></p>
          </div>
          <div>
            <p>_______________________</p>
            <p><strong>Contractor Signature</strong></p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const editorRef = ref(null);
const documentHtml = ref('');
const historyStack = ref([]);

const saveState = () => {
  if (editorRef.value) {
    historyStack.value.push(editorRef.value.innerHTML);
  }
};

const undoLastAction = () => {
  if (historyStack.value.length > 0) {
    const previousState = historyStack.value.pop();
    editorRef.value.innerHTML = previousState;
    documentHtml.value = previousState; 
  }
};

const syncState = () => {
  if (editorRef.value) {
    documentHtml.value = editorRef.value.innerHTML;
  }
};

const wrapSelection = (cssClass, dataAttribute = null, dataValue = null) => {
  const selection = window.getSelection();

  if (!selection.rangeCount || selection.isCollapsed) {
    alert('Please highlight some text first!');
    return;
  }

  const range = selection.getRangeAt(0);

  if (!editorRef.value.contains(range.commonAncestorContainer)) return;

  saveState(); // Save to undo stack before making changes!

  const walker = document.createTreeWalker(
    range.commonAncestorContainer,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: function(node) {
        return range.intersectsNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    },
    false
  );

  const nodesToWrap = [];
  let currentNode;
  while ((currentNode = walker.nextNode())) {
    nodesToWrap.push(currentNode);
  }

  if (nodesToWrap.length === 0) return;

  const startNode = nodesToWrap[0];
  const endNode = nodesToWrap[nodesToWrap.length - 1];

  if (range.endContainer === endNode && endNode.nodeType === Node.TEXT_NODE) {
    endNode.splitText(range.endOffset);
  }

  if (range.startContainer === startNode && startNode.nodeType === Node.TEXT_NODE) {
    const rightPart = startNode.splitText(range.startOffset);
    nodesToWrap[0] = rightPart; 
  }

  nodesToWrap.forEach(textNode => {
    if (textNode.textContent.trim().length === 0) return; 

    const span = document.createElement('span');
    span.className = cssClass;
    
    if (dataAttribute && dataValue) {
      span.setAttribute(dataAttribute, dataValue);
    }

    textNode.parentNode.replaceChild(span, textNode);
    span.appendChild(textNode);
  });

  syncState(); 
  selection.removeAllRanges(); 
};

// --- CORE ACTIONS ---

const applyStrikeThrough = () => wrapSelection('strike-redline');

const applyComment = () => {
  const selection = window.getSelection();
  if (selection.isCollapsed) {
    alert('Please highlight the text you want to comment on.');
    return;
  }
  if (!editorRef.value.contains(selection.anchorNode)) return;

  const commentText = prompt('Enter your comment for this section:');
  if (commentText) {
    wrapSelection('comment-highlight', 'data-comment', commentText);
  }
};

// NEW: Combined Strike & Comment
const applyStrikeWithComment = () => {
  const selection = window.getSelection();
  if (selection.isCollapsed) {
    alert('Please highlight the text you want to strike and comment on.');
    return;
  }
  if (!editorRef.value.contains(selection.anchorNode)) return;

  const commentText = prompt('Reason for striking this out:');
  if (commentText) {
    wrapSelection('strike-comment-highlight', 'data-comment', commentText);
  }
};
</script>

<style scoped>
/* App Wrapper */
.pdf-viewer-app {
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #525659;
  margin: -8px;
}

/* Dark Toolbar */
.pdf-toolbar {
  background: #323639;
  padding: 10px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  z-index: 10;
}

.button-group {
  display: flex;
  gap: 12px;
}

.divider {
  width: 1px;
  height: 24px;
  background-color: #5f6368;
  margin: 0 10px;
}

.pdf-toolbar button {
  background: #44484b;
  border: 1px solid transparent;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #f1f3f4;
  transition: all 0.2s;
}

.pdf-toolbar button:hover:not(.disabled) {
  background: #5a5f63;
  border-color: rgba(255,255,255,0.2);
}

.pdf-toolbar button.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pdf-toolbar button.btn-strike { color: #ff8a80; }
.pdf-toolbar button.btn-comment { color: #ffe57f; }
.pdf-toolbar button.btn-strike-comment { color: #ffab40; } /* Orange hue for the combo */

/* Scrollable Canvas Area */
.pdf-canvas {
  flex: 1;
  overflow-y: auto;
  padding: 40px 20px;
  display: flex;
  justify-content: center;
}

.pdf-page {
  background: #ffffff;
  width: 21cm;
  min-height: 29.7cm;
  padding: 2.54cm;
  box-sizing: border-box;
  box-shadow: 0 4px 12px rgba(0,0,0,0.5);
  color: #333;
  line-height: 1.6;
  font-size: 11pt;
}

.pricing-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 15px;
  font-size: 10.5pt;
}

.pricing-table th, .pricing-table td {
  border: 1px solid #ddd;
  padding: 10px 14px;
  text-align: left;
}

.pricing-table th {
  background-color: #f1f3f4;
  font-weight: bold;
  color: #2c3e50;
}

/* --- DYNAMIC HIGHLIGHT STYLES --- */

/* 1. Pure Strike-Through */
:deep(.strike-redline) {
  text-decoration: line-through;
  color: #c0392b;
  background-color: #fadbd8;
}

/* 2. Pure Comment */
:deep(.comment-highlight) {
  background-color: #f1c40f;
  cursor: help;
  border-bottom: 2px dashed #d35400;
  position: relative;
}

/* 3. NEW: Strike + Comment Combo */
:deep(.strike-comment-highlight) {
  text-decoration: line-through;
  color: #c0392b; /* Red struck text */
  background-color: #f1c40f; /* Yellow comment background */
  cursor: help;
  border-bottom: 2px dashed #d35400;
  position: relative;
}

/* Tooltip behavior shared by both comment types */
:deep(.comment-highlight:hover::after),
:deep(.strike-comment-highlight:hover::after) {
  content: attr(data-comment);
  position: absolute;
  background: #333;
  color: #fff;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  top: 100%;
  left: 0;
  margin-top: 6px;
  white-space: nowrap;
  z-index: 20;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  /* Reset text-decoration inside the tooltip so the comment itself doesn't get struck through */
  text-decoration: none; 
}
</style>